/**
 * ═══════════════════════════════════════
 * Componente: AvailabilityCalendar
 * ═══════════════════════════════════════
 *
 * Calendario mensual que muestra la disponibilidad
 * de un tour. Se usa en el sidebar de single-tour.php.
 *
 * Consulta la REST API para obtener cupos por día.
 *
 * @package AusangateExpeditions
 * @since   1.0.0
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

// Nombres de meses y días en español
const MESES = [
	'January', 'February', 'March', 'April', 'May', 'June',
	'July', 'August', 'September', 'October', 'November', 'December',
];
const DIAS_SEMANA = [ 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su' ];

/**
 * Obtener todos los días del mes para el calendario
 */
function getDiasDelMes( anio, mes ) {
	const primerDia  = new Date( anio, mes, 1 );
	const ultimoDia  = new Date( anio, mes + 1, 0 );
	const diasEnMes  = ultimoDia.getDate();

	// Día de la semana del primer día (0=Do, lunes=1)
	let inicioSemana = primerDia.getDay();
	inicioSemana = inicioSemana === 0 ? 6 : inicioSemana - 1; // Ajustar para que Lunes = 0

	const dias = [];

	// Días vacíos antes del primer día
	for ( let i = 0; i < inicioSemana; i++ ) {
		dias.push( null );
	}

	// Días del mes
	for ( let d = 1; d <= diasEnMes; d++ ) {
		dias.push( d );
	}

	return dias;
}

/**
 * Formatear fecha como YYYY-MM-DD
 */
function formatFecha( anio, mes, dia ) {
	return `${ anio }-${ String( mes + 1 ).padStart( 2, '0' ) }-${ String( dia ).padStart( 2, '0' ) }`;
}

export default function AvailabilityCalendar({
	tourId,
	cuposMaximos,
	restUrl,
	bookingUrl,
}) {
	const hoy = new Date();

	const [ mesActual, setMesActual ]         = useState( hoy.getMonth() );
	const [ anioActual, setAnioActual ]       = useState( hoy.getFullYear() );
	const [ disponibilidad, setDisponibilidad ] = useState({});
	const [ cargando, setCargando ]           = useState( false );
	const [ fechaSeleccionada, setFechaSeleccionada ] = useState( '' );

	/**
	 * Consultar disponibilidad del mes actual
	 */
	const cargarDisponibilidad = useCallback( async () => {
		if ( ! tourId ) return;

		setCargando( true );

		try {
			const mesStr = `${ anioActual }-${ String( mesActual + 1 ).padStart( 2, '0' ) }`;
			const url    = `${ restUrl.replace( /\/$/, '' ) }/turismo/v1/disponibilidad?tour_id=${ tourId }&mes=${ mesStr }`;
			const res    = await fetch( url, { credentials: 'same-origin' } );
			const data   = await res.json();

			if ( data.success && data.data?.dias ) {
				setDisponibilidad( data.data.dias );
			}
		} catch ( err ) {
			console.error( 'Error loading availability:', err );
		} finally {
			setCargando( false );
		}
	}, [ tourId, anioActual, mesActual, restUrl ] );

	useEffect( () => {
		cargarDisponibilidad();
	}, [ cargarDisponibilidad ] );

	/**
	 * Navegar entre meses
	 */
	const mesAnterior = () => {
		if ( mesActual === 0 ) {
			setMesActual( 11 );
			setAnioActual( anioActual - 1 );
		} else {
			setMesActual( mesActual - 1 );
		}
	};

	const mesSiguiente = () => {
		if ( mesActual === 11 ) {
			setMesActual( 0 );
			setAnioActual( anioActual + 1 );
		} else {
			setMesActual( mesActual + 1 );
		}
	};

	// No permitir navegar a meses pasados
	const puedRetroceder = anioActual > hoy.getFullYear() ||
		( anioActual === hoy.getFullYear() && mesActual > hoy.getMonth() );

	/**
	 * Verificar si un día es pasado
	 */
	const esPasado = ( dia ) => {
		if ( ! dia ) return true;
		const fecha = new Date( anioActual, mesActual, dia );
		const hoyFecha = new Date( hoy.getFullYear(), hoy.getMonth(), hoy.getDate() );
		return fecha <= hoyFecha;
	};

	/**
	 * Obtener info de cupos para un día
	 */
	const getCupos = ( dia ) => {
		const fechaStr = formatFecha( anioActual, mesActual, dia );
		return disponibilidad[ fechaStr ] ?? null;
	};

	/**
	 * Clase CSS según disponibilidad
	 */
	const getClaseDia = ( dia ) => {
		if ( ! dia ) return '';
		if ( esPasado( dia ) ) return 'text-gray-300 cursor-not-allowed';

		const cupos = getCupos( dia );
		const fechaStr = formatFecha( anioActual, mesActual, dia );

		let clase = 'cursor-pointer hover:bg-primary/10 transition-colors rounded-lg ';

		if ( fechaStr === fechaSeleccionada ) {
			clase += 'bg-primary text-white font-bold ';
		} else if ( cupos !== null ) {
			if ( cupos <= 0 ) {
				clase += 'bg-red-50 text-red-400 line-through cursor-not-allowed ';
			} else if ( cupos <= 3 ) {
				clase += 'bg-warning/10 text-warning font-medium ';
			} else {
				clase += 'bg-success/10 text-success font-medium ';
			}
		}

		return clase;
	};

	const dias = getDiasDelMes( anioActual, mesActual );

	return (
		<div className="space-y-4">
			{/* Header con navegación */}
			<div className="flex items-center justify-between">
				<button
					onClick={ mesAnterior }
					disabled={ ! puedRetroceder }
					className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
					aria-label="Previous month"
				>
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M15 19l-7-7 7-7" />
					</svg>
				</button>

				<span className="font-bold text-dark">
					{ MESES[ mesActual ] } { anioActual }
				</span>

				<button
					onClick={ mesSiguiente }
					className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-background transition-colors"
					aria-label="Next month"
				>
					<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M9 5l7 7-7 7" />
					</svg>
				</button>
			</div>

			{/* Días de la semana */}
			<div className="grid grid-cols-7 gap-1 text-center">
				{ DIAS_SEMANA.map( ( dia ) => (
					<div key={ dia } className="text-xs font-medium text-muted py-1">
						{ dia }
					</div>
				)) }
			</div>

			{/* Grid de días */}
			<div className="grid grid-cols-7 gap-1 text-center relative">
				{ cargando && (
					<div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-lg z-10">
						<div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
					</div>
				) }

				{ dias.map( ( dia, idx ) => (
					<button
						key={ idx }
						disabled={ ! dia || esPasado( dia ) || ( getCupos( dia ) !== null && getCupos( dia ) <= 0 ) }
						onClick={ () => {
							if ( dia && ! esPasado( dia ) ) {
								const f = formatFecha( anioActual, mesActual, dia );
								setFechaSeleccionada( f );
							}
						} }
						className={ `w-full aspect-square flex items-center justify-center text-sm ${ getClaseDia( dia ) }` }
					>
						{ dia || '' }
					</button>
				)) }
			</div>

			{/* Leyenda */}
			<div className="flex flex-wrap gap-3 text-xs text-muted pt-2">
				<span className="flex items-center gap-1">
					<span className="w-3 h-3 rounded bg-success/20" /> Available
				</span>
				<span className="flex items-center gap-1">
					<span className="w-3 h-3 rounded bg-warning/20" /> Limited spots
				</span>
				<span className="flex items-center gap-1">
					<span className="w-3 h-3 rounded bg-red-100" /> Sold out
				</span>
			</div>

			{/* Botón de reservar */}
			{ fechaSeleccionada && (
				<a
					href={ `${ bookingUrl }?tour_id=${ tourId }&fecha=${ fechaSeleccionada }` }
					className="btn btn-primary w-full text-center mt-2"
				>
					Book for { new Date( fechaSeleccionada + 'T12:00:00' ).toLocaleDateString( 'en-US', { day: 'numeric', month: 'long' } ) }
				</a>
			) }
		</div>
	);
}

AvailabilityCalendar.propTypes = {
	tourId: PropTypes.number.isRequired,
	cuposMaximos: PropTypes.number,
	restUrl: PropTypes.string.isRequired,
	bookingUrl: PropTypes.string,
};

AvailabilityCalendar.defaultProps = {
	cuposMaximos: 20,
	bookingUrl: '/reservar/',
};
